import { Button } from "@/components/ui/button/button";
import FloatingInput from "@/components/ui/floatingInput";
import { FormField } from "@/components/ui/form/form";
import SubmitButton from "@/components/ui/submit-button";
import { useToast } from "@/components/ui/use-toast";
import { useResetPassword } from "@/features/recover-password/useResetPassword";
import { routes } from "@/router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PasswordWithRequirements from "@/components/ui/passwordRequirements";
import { useState } from "react";

const formSchema = z
	.object({
		password: z.string(),
		confirmPassword: z.string(),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

type FormData = z.infer<typeof formSchema>;

type ApiError = {
	code: number;
	message: string;
	details: Record<string, unknown> | null;
};

export default function ResetPasswordPage() {
	const navigate = useNavigate();
	const search = useSearch({ from: routes.public.resetPassword.id });
	const resetPasswordMutation = useResetPassword();
	const { toast } = useToast();

	const { control, handleSubmit, formState, watch } = useForm<FormData>({
		resolver: zodResolver(formSchema),
	});

	const [isPasswordValid, setIsPasswordValid] = useState(false);

	const password = watch("password", "");
	const confirmPassword = watch("confirmPassword", "");

	const isConfirmMatch = password === confirmPassword;
	const isFormValid = isPasswordValid && isConfirmMatch;

	const onSubmit = async (data: FormData) => {
		if (!search.token) {
			toast({
				title: "Invalid Request",
				description: "",
				variant: "destructive",
			});
			return;
		}

		resetPasswordMutation.mutate(
			{ token: search.token, password: data.password },
			{
				onSuccess: () => {
					toast({
						title: "Password Reset Successful",
						description: "Your password has been reset successfully.",
					});
					navigate({ to: routes.public.signMail.id, replace: true });
				},
				onError: (error: ApiError) => {
					toast({
						title: "Password Reset Failed",
						description: error.message || "Failed to reset password",
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<div className="flex flex-col items-center justify-between px-6 py-8 bg-white text-textDark h-[100svh] ">
			<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
				<div className="flex flex-col space-y-2 text-center">
					<h1 className="text-2xl font-semibold tracking-tight">
						Reset Password
					</h1>
					<p className="text-sm text-muted-foreground">
						Enter your new password below
					</p>
				</div>

				<div className="grid gap-6 pt-24">
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="grid gap-4">
							<div className="grid gap-2">
								<FormField
									control={control}
									name="password"
									render={({ field }) => (
										<PasswordWithRequirements
											label="Password"
											value={field.value ?? ""}
											onChange={field.onChange}
											error={formState.errors.password}
											onValidationChange={setIsPasswordValid}
										/>
									)}
								/>
							</div>

							<div className="grid gap-2">
								<FormField
									control={control}
									name="confirmPassword"
									render={({ field }) => (
										<FloatingInput
											label="Confirm your new password"
											type="password"
											value={field.value}
											onChange={field.onChange}
											error={formState.errors.confirmPassword}
										/>
									)}
								/>
							</div>

							<SubmitButton
								mutationStatus={resetPasswordMutation.status}
								className="w-full"
								disabled={!isFormValid}
							>
								Reset Password
							</SubmitButton>
						</div>
					</form>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background px-2 text-muted-foreground">
								Or
							</span>
						</div>
					</div>

					<Button
						variant="outline"
						onClick={() => navigate({ to: routes.public.passwordRecovery.id })}
					>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Password Recovery
					</Button>
				</div>
			</div>
		</div>
	);
}
